import React, { forwardRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Flex, Icon, Link, SimpleGrid, Text } from '@driftt/dds'
import { faSalesforce } from '@driftt/dds.icons/brands/faSalesforce'

import {
  AccountActivity,
  ActivityType,
  AIScoringTopActivities
} from 'modules/accountActivity/domain'
import { capitalizeStrings } from 'utils/formatStrings'
import { timeSinceUpdated } from 'utils/timeStamp'

const UnderlinedLink = forwardRef(({ children, ...rest }: any, ref) => (
  <Link
    css={{
      textDecoration: 'underline'
    }}
    type="subtle"
    size="small"
    ref={ref}
    {...rest}>
    {children}
  </Link>
))

const getTitleForActivity = (
  type: ActivityType | string,
  role: string,
  attributes: Partial<AIScoringTopActivities['attributes']>
) => {
  const { categorizedUrl = '', pageTitle, endUserName, endUserEmail } = attributes || {}
  let formattedRole

  if (role && role !== 'other') {
    formattedRole = capitalizeStrings(role)
  } else if (endUserName) {
    formattedRole = endUserName.indexOf('unknown') > -1 ? endUserEmail : endUserName
  } else {
    formattedRole = 'Site Visitor'
  }

  switch (type) {
    case ActivityType.CONVERSATION:
    case 'other chatbot conversation':
      return `Conversation with ${formattedRole}`
    case ActivityType.EMAIL_OPEN:
      return `Email opened by ${formattedRole}`
    case ActivityType.EMAIL_CLICK:
      return `Email click from ${formattedRole}`
    case ActivityType.MEETING_BOOKED:
      return `Meeting booked with ${formattedRole}`
    case ActivityType.PAGE_VIEW:
      return categorizedUrl !== pageTitle
        ? `${capitalizeStrings(
            categorizedUrl.split('_').join(' ')
          )} page visit from ${formattedRole}`
        : `Page visit from ${formattedRole}`
    case ActivityType.VIDEO:
      return `Video view from ${formattedRole}`
    case 'webinar':
      return `Webinar activity from ${formattedRole}`
    case 'email click':
      return `Email click from ${formattedRole}`
    case 'email open':
      return `Email open from ${formattedRole}`
    case 'email inbound':
      return `Inbound email from ${formattedRole}`
    case 'sms inbound':
      return `Inbound SMS from ${formattedRole}`
    case 'fill out form':
      return `Form filled by ${formattedRole}`
    case 'unsubscribed':
      return `Unsubscribe from ${formattedRole}`
    default:
      return ''
  }
}

const getActivityMetaData = (
  type: ActivityType | string,
  time: number,
  attributes: Partial<AIScoringTopActivities['attributes']>
) => {
  const activityDate = `${timeSinceUpdated(time)} ago`
  const {
    pageTitle = '',
    pageUrl,
    endUserName,
    endUserId,
    videoTitle,
    videoUrl,
    conversationId,
    conversationPreview,
    endUserEmail
  } = attributes || {}

  switch (type) {
    case ActivityType.MEETING_BOOKED:
      return (
        <>
          {(endUserName || endUserEmail) && (
            <Text as="span" variant="metadata" truncate>
              <RouterLink component={UnderlinedLink} to={`/activity/contacts/${endUserId}`}>
                {endUserName || endUserEmail}
              </RouterLink>
            </Text>
          )}

          <Text as="span" css={{ flex: 'none' }} variant="metadata">
            {endUserName ? ` • ${activityDate}` : activityDate}
          </Text>
        </>
      )
    case ActivityType.CONVERSATION:
      return (
        <>
          <Text css={{ flex: 'none' }} as="span" variant="metadata">
            <RouterLink component={UnderlinedLink} to={`/activity/contacts/${endUserId}`}>
              {endUserName ?? (endUserEmail || 'Site Visitor')}
            </RouterLink>{' '}
            said
          </Text>

          {conversationPreview && (
            <Text as="span" variant="metadata" truncate>
              <RouterLink component={UnderlinedLink} to={`/conversations/${conversationId}`}>
                &quot;{conversationPreview}&quot;
              </RouterLink>
            </Text>
          )}

          <Text as="span" css={{ flex: 'none' }} variant="metadata">{` • ${activityDate}`}</Text>
        </>
      )
    case ActivityType.PAGE_VIEW:
      return (
        <>
          {(endUserName || endUserEmail) && (
            <Text css={{ flex: 'none' }} as="span" variant="metadata">
              <RouterLink component={UnderlinedLink} to={`/activity/contacts/${endUserId}`}>
                {endUserName || endUserEmail}
              </RouterLink>
              {` •`}
            </Text>
          )}
          {pageTitle && (
            <Text as="span" variant="metadata" truncate>
              &quot;
              <UnderlinedLink href={pageUrl} type="subtle" target="_blank" external size="small">
                {capitalizeStrings(pageTitle)}
              </UnderlinedLink>
              &quot;
            </Text>
          )}

          <Text as="span" css={{ flex: 'none' }} variant="metadata">{` • ${activityDate}`}</Text>
        </>
      )
    case ActivityType.VIDEO:
      return (
        <>
          <Text as="span" variant="metadata" truncate>
            <Link
              css={{
                textDecoration: 'underline',
                paddingBottom: '$half'
              }}
              type="subtle"
              href={videoUrl}
              target="_blank"
              external
              size="small">
              {videoTitle}
            </Link>
          </Text>

          <Text as="span" css={{ flex: 'none' }} variant="metadata">{` • ${activityDate}`}</Text>
        </>
      )
    case ActivityType.EMAIL_CLICK:
      return (
        <>
          {(endUserName || endUserEmail) && (
            <Text css={{ flex: 'none' }} as="span" variant="metadata">
              <RouterLink component={UnderlinedLink} to={`/activity/contacts/${endUserId}`}>
                {endUserName || endUserEmail}
              </RouterLink>
              {` •`}
            </Text>
          )}
          <Text as="span" variant="metadata">
            {activityDate}
          </Text>
        </>
      )
    case ActivityType.EMAIL_OPEN:
      return (
        <Text as="span" variant="metadata">
          {activityDate}
        </Text>
      )
    default:
      return (
        <>
          {(endUserName || endUserEmail) && (
            <Text css={{ flex: 'none', maxWidth: '160px' }} as="span" variant="metadata" truncate>
              {endUserName && endUserName.indexOf('unknown') > -1 ? endUserEmail : endUserName}
              {` •`}
            </Text>
          )}
          <Text as="span" variant="metadata">
            {activityDate} {` •`}
          </Text>

          <Icon
            css={{
              color: '$gray8'
            }}
            icon={faSalesforce}
            size="large"
            height={20}
          />
        </>
      )
  }
}

const parseActivities = (
  acc: any,
  { activity, activityId, categorizedRole, intent, receivedAt, attributes }: AIScoringTopActivities
) => {
  // Filter out null activities and other
  if (activity === 'other' || !activity) {
    return acc
  }

  return [
    ...acc,
    {
      id: `${activityId}-${receivedAt}`,
      receivedAt,
      isHighIntent: intent,
      metaData: getActivityMetaData(activity, receivedAt, attributes),
      title: getTitleForActivity(activity, categorizedRole, attributes)
    }
  ]
}

const ExplainAIEngagement = ({
  accountActivity: { accountScoredActivity },
  truncate,
  limit
}: {
  accountActivity: AccountActivity
  truncate: boolean
  limit?: number
}) => {
  if (!accountScoredActivity?.topActivities?.length) {
    return null
  }

  let activityEvents = accountScoredActivity.topActivities
    .reduce(parseActivities, [])
    .sort((a: any, b: any) => {
      if (a.isHighIntent && !b.isHighIntent) {
        return -1
      }

      if (!a.isHighIntent && b.isHighIntent) {
        return 1
      }

      if (a.isHighIntent && b.isHighIntent) {
        return a.receivedAt > b.receivedAt ? -1 : 1
      }

      if (!a.isHighIntent && !b.isHighIntent) {
        return a.receivedAt > b.receivedAt ? -1 : 1
      }

      return 0
    })

  if (limit) {
    activityEvents = activityEvents.splice(0, limit)
  }

  return (
    <SimpleGrid
      css={{
        overflowY: truncate ? 'hidden' : 'auto',
        maxHeight: '300px'
      }}
      columns="1">
      {activityEvents.map(({ metaData, id, title, isHighIntent }: any) => (
        <Box
          css={{
            borderBottom: 'solid 1px $gray2',
            overflow: 'hidden',
            padding: '$1 0',
            '&:last-child': {
              borderBottom: limit ? 'solid 1px $gray2' : 'none'
            }
          }}
          key={id}>
          <SimpleGrid
            columns="2"
            justifyItems="start"
            css={{
              width: '100%;',
              overflow: 'hidden',
              paddingBottom: '$half',
              gridTemplateColumns: 'auto 1fr',
              gap: '$1',
              gridAutoRows: 'auto',
              '&:last-child': {
                paddingBottom: limit ? '$half' : 'none'
              }
            }}>
            {isHighIntent && (
              <Text
                as="span"
                css={{
                  background: '$purple2',
                  padding: '2px $half',
                  borderRadius: '$1',
                  height: 'fit-content',
                  alignSelf: 'center'
                }}>
                High Intent
              </Text>
            )}
            <Text as="span" truncate={truncate}>
              {title}
            </Text>
          </SimpleGrid>

          <Flex grow={1} css={{ width: '100%;', overflow: 'hidden' }}>
            {metaData}
          </Flex>
        </Box>
      ))}
    </SimpleGrid>
  )
}

export default ExplainAIEngagement
